import { Suspense, useEffect } from "react";
import { registerDeployment } from "@/lib/registerDeployment";
import { lazyWithRetry } from "@/lib/lazyWithRetry";

import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { ThemeProvider } from "@/hooks/useTheme";

import DynamicFavicon from "@/components/DynamicFavicon";
import AuthHashRedirect from "@/components/AuthHashRedirect";
import AuthEventRedirect from "@/components/AuthEventRedirect";

const Index = lazyWithRetry(() => import("./pages/Index"));
const Auth = lazyWithRetry(() => import("./pages/Auth"));
const Admin = lazyWithRetry(() => import("./pages/Admin"));
const ArtworkDetail = lazyWithRetry(() => import("./pages/ArtworkDetail"));
const AdminArtworkEdit = lazyWithRetry(() => import("./pages/AdminArtworkEdit"));
const Pubblicazioni = lazyWithRetry(() => import("./pages/Pubblicazioni"));
const Artista = lazyWithRetry(() => import("./pages/Artista"));
const Ricerca = lazyWithRetry(() => import("./pages/Ricerca"));
const Luoghi = lazyWithRetry(() => import("./pages/Luoghi"));
const Modulistica = lazyWithRetry(() => import("./pages/Modulistica"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const HomepageBozza = lazyWithRetry(() => import("./pages/HomepageBozza"));
const Homepage = lazyWithRetry(() => import("./pages/Homepage"));


// Bump this string to invalidate every persisted cache after a breaking change.
const APP_CACHE_VERSION = "v1";
const ONE_DAY_MS = 1000 * 60 * 60 * 24;

// Keys that must NEVER be persisted to localStorage (sensitive / user-scoped).
const NON_PERSISTED_KEYS = new Set([
  "leads",
  "visitor-leads",
  "sensitive",
  "auth",
  "user-role",
]);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 min: fresh window, no refetch
      gcTime: ONE_DAY_MS, // must be >= persister maxAge
      refetchOnWindowFocus: false,
    },
  },
});

const persister =
  typeof window !== "undefined"
    ? createSyncStoragePersister({
        storage: window.localStorage,
        key: "archivio-coltella-query-cache",
        throttleTime: 1000,
      })
    : undefined;

const App = () => {
  useEffect(() => {
    registerDeployment();
  }, []);

  const content = (

    <ThemeProvider>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner position="bottom-right" />
          <DynamicFavicon />
          <BrowserRouter>
            <AuthHashRedirect />
            <AuthEventRedirect />
            <Suspense fallback={null}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/opera/:id" element={<ArtworkDetail />} />
                <Route path="/pubblicazioni" element={<Pubblicazioni />} />
                <Route path="/artista" element={<Artista />} />
                <Route path="/ricerca" element={<Ricerca />} />
                <Route path="/luoghi" element={<Luoghi />} />
                <Route path="/modulistica" element={<Modulistica />} />

                <Route path="/auth" element={<Auth />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="/admin/opera/:id" element={<AdminArtworkEdit />} />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="/homepage-bozza" element={<HomepageBozza />} />
                <Route path="/homepage" element={<Homepage />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </ThemeProvider>
  );

  if (!persister) {
    // SSR / non-browser fallback (shouldn't happen in this SPA, but safe).
    return content;
  }

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        maxAge: ONE_DAY_MS,
        buster: APP_CACHE_VERSION,
        dehydrateOptions: {
          shouldDehydrateQuery: (query) => {
            if (query.state.status !== "success") return false;
            const firstKey = Array.isArray(query.queryKey)
              ? String(query.queryKey[0] ?? "")
              : String(query.queryKey);
            return !NON_PERSISTED_KEYS.has(firstKey);
          },
        },
      }}
    >
      {content}
    </PersistQueryClientProvider>
  );
};

export default App;
